import React from "react";
import { Box } from "@chakra-ui/react";
import { type CaseStudySimple, type BlogSimple } from "lib/sanity";
import { Section, sectionSpacingY } from "components/section";
import { CenteredQuote } from "components/quotes";
import { CaseStudySlider, CustomerStats, SecurityFeatures } from "components/content";
import { DecorativeBackgroundImage } from "components/images";
import { Rule } from "ui/rule";
import { SolidPlate } from "ui/plates";
import { getSafeCaseStudy } from "utils";
import { AidInsightsSlideshow } from "./aid-insights";
import { Faq } from "./faq";
import { AidDemoVideoSection } from "./aid-demo-video-section";
import { AidSisyphus } from "./aid-sisyphus";
import { AidAgents } from "./aid-agents";
import { AidSteps } from "./aid-steps";
import { AidTools } from "./aid-tools";
import { AidResources } from "./aid-resources";
import { AidComparisonTable } from "./aid-comparisons";

type AidTemplateProps = {
  /** The product page uses the demo video in the hero whereas ads have a custom hero, and the demo video in the template.  */
  isAdsPage?: boolean;
  blogs: BlogSimple[];
  caseStudies: CaseStudySimple[];
};

export function AidTemplate({ isAdsPage, caseStudies, blogs }: AidTemplateProps) {
  const fundrise = getSafeCaseStudy(caseStudies, "fundrise");
  const whoop = getSafeCaseStudy(caseStudies, "whoop-ai-decisioning");
  const petsmart = getSafeCaseStudy(caseStudies, "petsmart");

  return (
    <Box>
      {isAdsPage && (
        <AidDemoVideoSection
          hasAuraPlate
          bg="new.violet.150"
          themeBgColor="new.violet.150"
          title="AI marketing that drives real results"
          textMaxW="100%"
          mt={sectionSpacingY}
          pb={{ base: "small", md: "big" }}
        />
      )}
      <AidSisyphus />

      <Section bg="new.violet.950" themeBgColor="new.violet.950" theme="dark" pt={{ base: "biggest", md: "huge" }}>
        <CenteredQuote
          theme="dark"
          maxW="900px"
          quoteText="We saw more learnings in 6 weeks with Hightouch AI Decisioning than in the previous 12 months of experiments on our own. Now our marketers are focusing on strategy, not operations."
          author={{ name: "Aoife O’Driscoll", job: "AVP Lifecycle Marketing" }}
          company={{ name: "Whoop", logo: whoop.customer.logoUrl }}
        />
      </Section>

      <CustomerStats
        bg="new.violet.950"
        themeBgColor="new.violet.950"
        theme="dark"
        my="0"
        py="0"
        customers={[whoop.customer, fundrise.customer]}
        customStats={[
          {
            customer: petsmart.customer,
            title: "22%",
            description: (
              <>
                Increase in loyalty <br />
                offer activation
              </>
            ),
            href: "https://www.modernretail.co/marketing/how-petsmart-is-using-ai-in-its-revamped-loyalty-program/",
          },
        ]}
      />

      <AidAgents />
      <AidSteps />
      <Section bg="new.violet.950" themeBgColor="new.violet.950" theme="dark" py="0">
        <Rule theme="dark" />
        <SecurityFeatures
          noContainerPx
          hasHipaa={false}
          my="0"
          pt="big"
          pb={{ base: "huge", md: "huger" }}
          direction="row"
          theme="dark"
          bgColor="new.violet.950"
          themeBgColor="new.violet.950"
          title="Keep your data where it’s most secure"
          description="Hightouch doesn’t store your data. Instead we simply read from your data warehouse where it stays safe and sound."
        />
      </Section>
      <CaseStudySlider
        my={0}
        py={sectionSpacingY}
        title="Helping leading brands power personalization at massive scale"
        slides={[
          {
            title: fundrise.title,
            caseStudy: fundrise,
            media: <DecorativeBackgroundImage src={fundrise.fullImageUrl} />,
            url: `/customers/${fundrise.customer.slug}`,
          },

          {
            title: "PetSmart uses AI Decisioning to personalize journeys for 70 million loyalty program members",
            caseStudy: petsmart,
            media: <DecorativeBackgroundImage src={petsmart.fullImageUrl} />,
            url: "https://www.modernretail.co/marketing/how-petsmart-is-using-ai-in-its-revamped-loyalty-program/",
          },
          {
            title: whoop.title,
            caseStudy: whoop,
            media: <DecorativeBackgroundImage src={whoop.fullImageUrl} />,
            url: `/customers/${whoop.customer.slug}`,
          },
        ]}
      />
      <SolidPlate id="insights" bgColor="new.violet.900" theme="dark">
        <AidInsightsSlideshow />

        <Box as="section" mt={{ base: "biggest", md: "huge" }}>
          <Rule theme="dark" display={{ base: "block", md: "none" }} pb="biggest" />
          <CenteredQuote
            theme="dark"
            maxW="980px"
            quoteText="One of the most incredible aspects of AI Decisioning is that it uncovers hidden patterns and correlations that humans simply can’t detect and instantly feeds those insights back to our marketers so they can continuously optimize messaging, timing, and outcomes."
            author={{ name: "Luke Ruth", job: "Chief Product Officer" }}
            company={{ name: "Fundrise", logo: fundrise.customer.logoUrl }}
          />
        </Box>
      </SolidPlate>
      <AidTools />
      <AidComparisonTable />
      <AidResources resources={blogs} />

      <Faq />
    </Box>
  );
}
